/**
 * API client for booking app
 * Communicates with the main ExperienceLocal web app's widget API
 *
 * Supports API key authentication for enterprise integrations.
 * API key can be passed via:
 * - VITE_API_KEY environment variable
 * - ?apiKey=... URL parameter
 * - data-api-key attribute on embed script
 * - Programmatically via setApiKey()
 */

// In development, we'll try both common Next.js ports
const DEV_API_URLS = ['http://localhost:3000', 'http://localhost:3001'];
let workingApiBase: string | null = null;

// API Key storage - set once and used for all requests
let apiKeyValue: string | null = null;

/**
 * Set the API key for all future requests
 * Call this before making any API calls if using programmatic integration
 */
export function setApiKey(key: string | null): void {
  apiKeyValue = key;
  if (import.meta.env.DEV && key) {
    console.warn('[API] API key set:', key.substring(0, 15) + '...');
  }
}

/**
 * Get the currently configured API key
 */
export function getApiKey(): string | null {
  // Already set programmatically
  if (apiKeyValue) return apiKeyValue;

  // Check environment variable
  if (import.meta.env.VITE_API_KEY) {
    return import.meta.env.VITE_API_KEY;
  }

  // Check URL parameter (for embed usage)
  if (typeof window !== 'undefined') {
    const searchParams = new URLSearchParams(window.location.search);
    const urlApiKey = searchParams.get('apiKey');
    if (urlApiKey) return urlApiKey;

    // Check for data attribute from embed script
    const script = document.querySelector('script[data-api-key]');
    if (script) {
      const dataApiKey = script.getAttribute('data-api-key');
      if (dataApiKey) return dataApiKey;
    }
  }

  return null;
}

// Helper to create a timeout signal (browser compatible)
function createTimeoutSignal(ms: number): AbortSignal {
  const controller = new AbortController();
  setTimeout(() => controller.abort(), ms);
  return controller.signal;
}

// Get API base URL
function getApiBase(): string {
  // If we've already found a working URL, use it
  if (workingApiBase) return workingApiBase;
  
  // Check env var first
  if (import.meta.env.VITE_API_URL) {
    workingApiBase = import.meta.env.VITE_API_URL;
    return workingApiBase;
  }
  
  // In production, use the main API domain (www subdomain to avoid redirect)
  if (!import.meta.env.DEV) {
    workingApiBase = 'https://www.experiencelocal.io';
    return workingApiBase;
  }
  
  // In dev, return first option (will retry with others if it fails)
  return DEV_API_URLS[0];
}

/**
 * Build headers for API requests
 * Includes API key if available
 */
function getApiHeaders(additionalHeaders?: Record<string, string>): Record<string, string> {
  const headers: Record<string, string> = {
    ...additionalHeaders,
  };

  const apiKey = getApiKey();
  if (apiKey) {
    headers['x-api-key'] = apiKey;
  }

  return headers;
}

// Smart fetch that tries multiple dev URLs if needed
async function apiFetch(path: string, options?: RequestInit): Promise<Response> {
  // Build headers with API key
  const headers = getApiHeaders(options?.headers as Record<string, string> | undefined);
  const fetchOptions: RequestInit = {
    ...options,
    headers,
  };

  // In production or with explicit env var, just fetch directly
  if (!import.meta.env.DEV || import.meta.env.VITE_API_URL) {
    const url = `${getApiBase()}${path}`;
    console.warn(`[API] Fetching: ${url}`);
    return fetch(url, fetchOptions);
  }
  
  // If we have a known working base, try it first
  if (workingApiBase) {
    try {
      const url = `${workingApiBase}${path}`;
      console.warn(`[API] Trying cached base: ${url}`);
      const response = await fetch(url, options);
      // Check if we got JSON (working server)
      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        return response;
      }
      console.warn(`[API] Cached base returned non-JSON, clearing cache`);
    } catch (err) {
      console.warn(`[API] Cached base failed:`, err);
    }
    // Server is broken - clear cache and try others
    workingApiBase = null;
  }
  
  // Try each dev URL until one works
  const errors: string[] = [];
  for (const baseUrl of DEV_API_URLS) {
    try {
      const url = `${baseUrl}${path}`;
      console.warn(`[API] Trying: ${url}`);
      
      const response = await fetch(url, {
        ...fetchOptions,
        signal: createTimeoutSignal(5000),
      });
      
      // Check content type - we need JSON, not HTML error pages
      const contentType = response.headers.get('content-type') || '';
      console.warn(`[API] ${baseUrl} responded with content-type: ${contentType}`);
      
      if (contentType.includes('application/json')) {
        // Found a working server!
        workingApiBase = baseUrl;
        console.warn(`[API] ✓ Using API server at: ${baseUrl}`);
        return response;
      }
      
      // Got HTML response - server is broken, try next
      errors.push(`${baseUrl}: returned HTML instead of JSON`);
    } catch (err) {
      // CORS errors, network errors, timeouts all end up here
      // This is expected when the wrong server is running on a port
      const message = err instanceof Error ? err.message : 'Unknown error';
      errors.push(`${baseUrl}: ${message}`);
      console.warn(`[API] ${baseUrl} failed (likely CORS or wrong server):`, message);
      // Continue to next URL - don't throw yet
    }
  }
  
  // All URLs failed
  const errorMessage = `All API URLs failed:\n${errors.join('\n')}`;
  console.error(`[API] ${errorMessage}`);
  throw new Error(errorMessage);
}

// Debug logging on startup
if (import.meta.env.DEV) {
  console.warn('[API] Development mode - will auto-detect API server on port 3000 or 3001');
}


export interface PropertyConfig {
  property: {
    id: string;
    name: string;
    slug: string;
    description: string | null;
    images: Array<{ url: string; alt: string }> | null;
    location: {
      address: string | null;
      city: string | null;
      state: string | null;
      country: string | null;
    } | null;
    contact: {
      phone: string | null;
      email: string | null;
      website: string | null;
    } | null;
  };
  organization: {
    id: string;
    name: string;
    slug: string;
  };
  feeSettings?: {
    feeRate: number;
    feeDisplayMode: 'guest_pays' | 'hotel_absorbs';
    showFeeToGuest: boolean;
  };
  widgetSettings: {
    primaryColor: string;
    theme: 'light' | 'dark' | 'auto';
    layout: 'grid' | 'list' | 'calendar';
    columns: number;
    showImages: boolean;
    showPrices: boolean;
    showDuration: boolean;
    showRatings: boolean;
    enableSearch: boolean;
    enableCategoryFilter: boolean;
    showPoweredBy: boolean;
    mobileCarousel?: boolean;
  };
  categories: Array<{
    id: string;
    name: string;
    slug: string;
    icon?: string | null;
  }>;
  experienceCount: number;
  isDemo?: boolean;
}

export interface Experience {
  id: string;
  name: string;
  slug: string | null;
  shortDescription: string | null;
  description: string | null;
  durationMinutes: number;
  durationDisplay: string | null;
  duration?: string; // Alias for durationDisplay for backward compatibility
  basePrice: number;
  priceInCents?: number;
  currency: string;
  maxGuests: number;
  minGuests: number;
  maxCapacity?: number;
  priceType: 'per_person' | 'per_group';
  coverImage: string | null;
  images: Array<{ url: string; alt?: string; caption?: string }> | null;
  tags: string[] | null;
  categoryId: string | null;
  categoryName: string | null;
  categoryIcon: string | null;
  category: {
    id: string;
    name: string;
    icon: string | null;
  } | null;
  averageRating: number | null;
  totalReviews: number;
  highlights: unknown;

  // Curated experience fields
  type: 'first_party' | 'curated';
  source?: 'viator' | null;
  affiliateUrl?: string | null;
  conciergeNote?: string | null;
  isStaffPick?: boolean;
  isLocallyLoved?: boolean;
  customBadge?: string | null;
  inclusions?: string[] | null;
  exclusions?: string[] | null;
}

export interface ExperienceDetail extends Experience {
  highlights: string[] | null;
  whatToBring: string[] | null;
  included: string[] | null;
  requirements: string[] | null;
  meetingPoint: string | null;
  cancellationPolicy: string | null;
  addOns: Array<{
    id: string;
    name: string;
    description: string | null;
    price: number;
    priceType: 'per_person' | 'per_booking';
    priceInCents?: number;
    maxQuantity?: number | null;
    isRequired?: boolean;
  }>;
}

export interface TimeSlot {
  time: string;
  available: boolean;
  spotsRemaining: number;
  /** Inventory ID for this specific slot - required for booking */
  inventoryId: string;
}

export interface AvailabilityData {
  date: string;
  slots: TimeSlot[];
}

export interface ExperienceParams {
  category?: string;
  search?: string;
  date?: string;
  limit?: number;
  offset?: number;
  /** Guest stay start date (ISO format) - filters to experiences with availability */
  stayStartDate?: string;
  /** Guest stay end date (ISO format) - filters to experiences with availability */
  stayEndDate?: string;
  /** Minimum guests to check availability for */
  minGuests?: number;
}

export interface AvailabilityParams {
  date: string;
  month?: string;
}

/** Booking details returned from API for confirmation page */
export interface BookingDetails {
  id: string;
  bookingNumber: string;
  status: string;
  experience: {
    id: string;
    name: string;
    coverImage: string | null;
  };
  property: {
    id: string;
    name: string;
  };
  date: string;
  startTime: string;
  endTime: string | null;
  guestCount: number;
  guestInfo: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  };
  pricing: {
    currency: string;
    basePrice: number;
    addOnsTotal: number;
    discountAmount: number;
    taxAmount: number;
    /** Service fee paid by guest (null/0 if hotel absorbs) */
    serviceFee: number | null;
    /** Platform fee rate as decimal */
    serviceFeeRate: number | null;
    /** Who paid the platform fee */
    feePaidBy: 'guest' | 'hotel' | null;
    totalPrice: number;
  };
  createdAt: string;
  confirmedAt: string | null;
}

export interface CreateBookingInput {
  experienceId: string;
  date: string;
  time: string;
  guestCount: number;
  guestInfo: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  };
  addOns?: Array<{ id: string; quantity: number }>;
  discountCode?: string;
}

export interface PaymentIntentInput {
  bookingId: string;
}

/** Result from validating a discount code */
export interface DiscountValidationResult {
  valid: boolean;
  discountPercent?: number;
  discountAmount?: number;
  errorMessage?: string;
}

// ============================================
// RESOURCES TYPES
// ============================================

export interface ResourceLocation {
  id: string;
  name: string;
  type: string;
  description: string | null;
  mapImage: string | null;
  coverImage: string | null;
  amenities: string[] | null;
  displayOrder: number;
  resourceCount: number;
}

export interface ResourceType {
  id: string;
  name: string;
  category: string;
  description: string | null;
  shortDescription: string | null;
  tier: 'standard' | 'premium' | 'vip' | 'luxury';
  icon: string | null;
  coverImage: string | null;
  capacity: number;
  maxOccupancy: number | null;
  pricingType: 'full_day' | 'half_day' | 'hourly' | 'multi_day' | 'seasonal' | 'dynamic';
  basePrice: number;
  currency: string;
  tierPricing: unknown;
  durationOptions: unknown;
  timeSlots: unknown;
  includedAmenities: unknown;
  addOns: unknown;
  upsells: unknown;
  mapDisplay: unknown;
}

export interface Resource {
  id: string;
  name: string;
  code: string;
  mapPosition: { x: number; y: number; rotation?: number } | null;
  tier: 'standard' | 'premium' | 'vip' | 'luxury' | null;
  status: 'active' | 'inactive' | 'maintenance';
  section: string | null;
  row: string | null;
  isAccessible: boolean;
  additionalFeatures: string[] | null;
  publicNotes: string | null;
  customDisplay: unknown;
  typeId: string;
}

export interface ResourceInventorySlot {
  id: string;
  slotId: string | null;
  slotName: string | null;
  startTime: string;
  endTime: string;
  isAvailable: boolean;
  isBlocked: boolean;
  isHeld: boolean;
  price: number;
  bookingId: string | null;
}

export interface ResourceAvailability {
  resource: Resource;
  type: ResourceType | null;
  slots: ResourceInventorySlot[];
}

export interface ResourceLocationDetail {
  location: {
    id: string;
    name: string;
    type: string;
    description: string | null;
    mapImage: string | null;
    mapImageDimensions: unknown;
    mapSettings: unknown;
    amenities: string[] | null;
    coverImage: string | null;
    bookingRules: unknown;
  };
  types: ResourceType[];
  resources: Resource[];
  availability?: ResourceAvailability[];
  date?: string;
  currency: string;
}

export interface CreateResourceBookingInput {
  inventoryId: string;
  guestEmail: string;
  guestFirstName: string;
  guestLastName: string;
  guestPhone?: string;
  guestCount?: number;
  roomNumber?: string;
  reservationNumber?: string;
  tier?: 'standard' | 'premium' | 'vip' | 'luxury';
  addOns?: Array<{
    addOnId: string;
    name: string;
    quantity: number;
    unitPrice: number;
    totalPrice: number;
  }>;
  specialRequests?: string;
  discountCode?: string;
}

export interface ResourceBookingResult {
  booking: {
    id: string;
    bookingNumber: string;
    accessToken: string;
    status: string;
    paymentStatus: string;
    bookingDate: string;
    startTime: string;
    endTime: string;
    slotName: string | null;
    guestCount: number;
    tier: string;
    totalPrice: number;
    currency: string;
    expiresAt: Date;
  };
  resource: {
    id: string;
    name: string;
    code: string;
  };
  type: {
    id: string;
    name: string;
  };
  location: {
    id: string;
    name: string;
  };
  pricing: {
    basePrice: number;
    addOnsTotal: number;
    discountCode: string | null;
    discountAmount: number;
    taxAmount: number;
    taxBreakdown: Array<{
      taxRateId: string;
      name: string;
      taxType: string;
      rate: number;
      amount: number;
      jurisdiction?: string;
    }>;
    taxInclusive: boolean;
    taxMethod: 'stripe_tax' | 'manual';
    serviceFee: number;
    totalPrice: number;
  };
}

class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /**
   * Check if this is an authentication error (invalid/missing API key)
   */
  isAuthError(): boolean {
    return this.status === 401;
  }

  /**
   * Check if this is a forbidden error (API access denied)
   */
  isForbiddenError(): boolean {
    return this.status === 403;
  }

  /**
   * Check if this is a rate limit error
   */
  isRateLimitError(): boolean {
    return this.status === 429;
  }
}

export { ApiError };

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));

    // Provide better error messages for common API key issues
    let message = error.message || 'Request failed';
    const code = error.code || error.error;

    if (response.status === 401) {
      if (code === 'authentication_required') {
        message = 'API key required. Please configure an API key.';
      } else if (code === 'invalid_api_key') {
        message = 'Invalid or expired API key. Please check your configuration.';
      }
    } else if (response.status === 403) {
      if (code === 'api_access_denied') {
        message = 'API access not available on your plan. Please upgrade.';
      } else if (code === 'insufficient_scope') {
        message = `API key lacks required permission: ${error.scope}`;
      } else if (code === 'ip_not_allowed') {
        message = 'API requests not allowed from this IP address.';
      } else if (code === 'origin_not_allowed') {
        message = 'API requests not allowed from this domain.';
      }
    } else if (response.status === 429) {
      const retryAfter = error.retryAfter ?? 60;
      message = `Rate limit exceeded. Please wait ${retryAfter} seconds.`;
    }

    throw new ApiError(message, response.status, code);
  }
  return response.json();
}

export const api = {
  async getPropertyConfig(propertyKey: string): Promise<PropertyConfig> {
    const res = await apiFetch(`/api/widget/${propertyKey}`);
    return handleResponse<PropertyConfig>(res);
  },

  async getInstanceConfig(propertyKey: string, instanceKey: string): Promise<PropertyConfig> {
    const res = await apiFetch(`/api/widget/${propertyKey}/instances/${instanceKey}`);
    return handleResponse<PropertyConfig>(res);
  },

  async getExperiences(propertyKey: string, params?: ExperienceParams): Promise<Experience[]> {
    const searchParams = new URLSearchParams();
    if (params?.category) searchParams.set('category', params.category);
    if (params?.search) searchParams.set('search', params.search);
    if (params?.date) searchParams.set('date', params.date);
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));
    // Stay date filtering params
    if (params?.stayStartDate) searchParams.set('stayStartDate', params.stayStartDate);
    if (params?.stayEndDate) searchParams.set('stayEndDate', params.stayEndDate);
    if (params?.minGuests) searchParams.set('minGuests', String(params.minGuests));

    const path = `/api/widget/${propertyKey}/experiences${searchParams.toString() ? `?${searchParams}` : ''}`;
    const res = await apiFetch(path);
    const data = await handleResponse<{ experiences: Experience[] }>(res);
    return data.experiences;
  },

  async getExperience(propertyKey: string, experienceSlug: string): Promise<ExperienceDetail> {
    const res = await apiFetch(`/api/widget/${propertyKey}/experiences/${experienceSlug}`);
    const data = await handleResponse<{ experience: ExperienceDetail }>(res);
    return data.experience;
  },

  async getAvailability(
    propertyKey: string,
    experienceId: string,
    params: AvailabilityParams
  ): Promise<AvailabilityData[]> {
    const searchParams = new URLSearchParams();
    searchParams.set('date', params.date);
    if (params.month) searchParams.set('month', params.month);

    const res = await apiFetch(
      `/api/widget/${propertyKey}/experiences/${experienceId}/availability?${searchParams}`
    );
    return handleResponse<AvailabilityData[]>(res);
  },

  async createBooking(
    propertyKey: string,
    data: CreateBookingInput
  ): Promise<{ bookingId: string; bookingNumber: string }> {
    const res = await apiFetch(`/api/widget/${propertyKey}/bookings`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return handleResponse<{ bookingId: string; bookingNumber: string }>(res);
  },

  async createPaymentIntent(
    propertyKey: string,
    data: PaymentIntentInput
  ): Promise<{ clientSecret: string; amount: number }> {
    const res = await apiFetch(`/api/widget/${propertyKey}/payment-intent`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return handleResponse<{ clientSecret: string; amount: number }>(res);
  },

  async validateDiscountCode(
    propertyKey: string,
    code: string,
    experienceId: string
  ): Promise<DiscountValidationResult> {
    const res = await apiFetch(`/api/widget/${propertyKey}/discount-codes/validate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, experienceId }),
    });
    return handleResponse<DiscountValidationResult>(res);
  },

  async getBookingDetails(
    propertyKey: string,
    bookingNumber: string,
    token?: string
  ): Promise<BookingDetails> {
    const params = token ? `?token=${token}` : '';
    const res = await apiFetch(`/api/widget/${propertyKey}/bookings/${bookingNumber}${params}`);
    return handleResponse<BookingDetails>(res);
  },

  /**
   * Track an affiliate click for a curated experience
   * Returns the affiliate URL to redirect to
   */
  async trackAffiliateClick(
    propertyKey: string,
    curatedExperienceId: string,
    sessionId?: string
  ): Promise<{ affiliateUrl: string; clickId?: string | null }> {
    const res = await apiFetch(`/api/widget/${propertyKey}/affiliate-click`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        curatedExperienceId,
        sessionId: sessionId || getSessionId(),
      }),
    });
    return handleResponse<{ affiliateUrl: string; clickId?: string | null }>(res);
  },

  // ============================================
  // RESOURCES API
  // ============================================

  /**
   * Get all resource locations for a property
   */
  async getResourceLocations(propertyKey: string): Promise<ResourceLocation[]> {
    const res = await apiFetch(`/api/widget/${propertyKey}/resources`);
    const data = await handleResponse<{ locations: ResourceLocation[]; currency: string }>(res);
    return data.locations;
  },

  /**
   * Get a specific resource location with types, resources, and availability
   */
  async getResourceLocation(
    propertyKey: string,
    locationId: string,
    date?: string,
    typeId?: string
  ): Promise<ResourceLocationDetail> {
    const searchParams = new URLSearchParams({ locationId });
    if (date) searchParams.set('date', date);
    if (typeId) searchParams.set('typeId', typeId);

    const res = await apiFetch(`/api/widget/${propertyKey}/resources?${searchParams}`);
    return handleResponse<ResourceLocationDetail>(res);
  },

  /**
   * Create a resource booking
   */
  async createResourceBooking(
    propertyKey: string,
    data: CreateResourceBookingInput
  ): Promise<ResourceBookingResult> {
    const res = await apiFetch(`/api/widget/${propertyKey}/resources/book`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return handleResponse<ResourceBookingResult>(res);
  },
};

// Generate or retrieve a session ID for affiliate tracking
function getSessionId(): string {
  const storageKey = 'el_session_id';
  let sessionId = sessionStorage.getItem(storageKey);
  if (!sessionId) {
    sessionId = `sess_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
    sessionStorage.setItem(storageKey, sessionId);
  }
  return sessionId;
}
